import { History } from 'history';
import pick from 'lodash-es/pick';
import upperFirst from 'lodash-es/upperFirst';
import {
    FORUM_OTHER_TOPICS,
    FORUM_TOPICS_NOT_FOUND_query,
    FORUM_TOPICS_SEARCH_PLACEHOLDER,
    LOAD_PREVIOUS,
} from 'mk/autogenerated/translations/ForumTopics.4d26733623217eb50540d887a68cbe6e'
import { forumTopicsCategoryUrl } from 'mk2/apps/forum/urls';
import { InfiniteListLoadable } from 'mk2/components/InfiniteListLoadable';
import { Link } from 'mk2/components/Link';
import { ListItem, ListItemPlaceholder } from 'mk2/components/ListItem';
import { ListItemIcon } from 'mk2/components/ListItemIcon';
import { NoResults } from 'mk2/components/NoResults';
import { SearchBox } from 'mk2/components/SearchBox';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { interpolate } from 'mk2/services/i18n';
import { parse, stringify } from 'query-string';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Route } from 'react-router';
import { ListRowProps } from 'react-virtualized';
import styles from './ForumTopics.mscss';
import { Props } from './ForumTopicsPage';

const ROW_HEIGHT = 44;

export class ForumTopics extends React.PureComponent<Props> {

    public UNSAFE_componentWillMount() {
        const { queryFromState, queryFromUrl, onUpdateSearch } = this.props;

        if (queryFromUrl && queryFromUrl !== queryFromState) {
            onUpdateSearch(queryFromUrl);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { queryFromState, queryFromUrl, onUpdateSearch } = this.props;

        // After has changed
        if (this.props.after !== prevProps.after) {
            this.load();
        }

        // Update search if changed by URL
        if (queryFromUrl !== prevProps.queryFromUrl && queryFromUrl !== queryFromState) {
            onUpdateSearch(queryFromUrl);
        }
    }

    public componentWillUnmount() {
        const { onUpdateSearch } = this.props;
        onUpdateSearch('');
    }

    public render() {
        const { loadingState, items } = this.props;

        return (
            <div className={styles.ForumTopics}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!items && items.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <>
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast />
            </>
        );
    };

    private renderSuccess = () => {
        const {
            category, match: { params: { categorySlug } }, location,
            queryFromState, queryFromUrl, loadingState,
            items, hasNext, hasPrevious,
        } = this.props;

        // InfiniteScroll data
        // @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
        const rowCount = hasNext ? items.length + 1 : items.length;

        return (
            <>
                <Route exact path={forumTopicsCategoryUrl} children={this.renderHeaders} />

                <SearchBox
                    placeholder={FORUM_TOPICS_SEARCH_PLACEHOLDER}
                    query={queryFromState}
                    onChange={this.onSearchChange}
                    autoFocus={categorySlug === 'all' ? 'end' : false}
                />

                {hasPrevious && isSuccess(loadingState) && (
                    <div className={styles.ForumTopics__loadPrevious}>
                        <Link
                            className={styles.ForumTopics__loadPrevious__link}
                            to={{ ...location, search: `?${stringify({ ...parse(location.search), after: -items[0].id })}`}}
                            onClick={this.loadPrevious}
                        >
                            {LOAD_PREVIOUS}
                        </Link>
                    </div>
                )}

                {categorySlug !== 'all' ? (
                    category && (
                        <div className={styles.ForumTopics__space}>
                            <h1 className={styles.ForumTopics__space__text}>
                                {category.name}
                            </h1>
                        </div>
                    )
                ) : (
                    <div className={styles.ForumTopics__space}>
                        <h1 className={styles.ForumTopics__space__text}>
                            {FORUM_OTHER_TOPICS}
                        </h1>
                    </div>
                )}

                {items.length === 0 ? (
                    <NoResults message={interpolate(FORUM_TOPICS_NOT_FOUND_query, {query: queryFromUrl})} />
                ) : (
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={rowCount}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={(props: ListRowProps) => this.rowRenderer(props) /* tslint:disable-line */}
                    />
                )}
            </>
        );
    };

    private load = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { match: { params: { categorySlug } }, queryFromUrl, onLoad, requestUser, after } = this.props;
        onLoad(requestUser.username, categorySlug, queryFromUrl, after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { match: { params: { categorySlug } }, queryFromUrl, onLoad, requestUser, items } = this.props;
        const after = items[items.length - 1].id;
        onLoad(requestUser.username, categorySlug, queryFromUrl, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private loadPrevious = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { match: { params: { categorySlug } }, queryFromUrl, onLoad, requestUser, items } = this.props;
        const after = -items[0].id;
        onLoad(requestUser.username, categorySlug, queryFromUrl, after);
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.items.length;

    private rowRenderer = ({ index, key, style }) => {
        const { hasNext, items, itemToTopicUrl } = this.props;

        if (!this.isRowLoaded({ index })) {
            return (
                <ListItemPlaceholder key={key} style={style} icon isLast />
            );
        }

        const item = items[index];

        return (
            <ListItem
                count={item.numThreads}
                icon={<ListItemIcon lazy photo={item.coverPhoto} />}
                isLast={!hasNext && items.length === index + 1}
                key={key}
                style={style}
                to={itemToTopicUrl(item)}
            >
                {upperFirst(item.name)}
            </ListItem>
        );
    };

    private onSearchChange = (q: string) => {
        const { onSearch, requestUser, match: { params: { categorySlug } } } = this.props;
        onSearch(requestUser.username, categorySlug, q);
    };

    private renderHeaders = ({ match }) => {
        const { loadingState, hasNext, hasPrevious, baseUrl, match: { params: { categorySlug } }, items, location } = this.props;
        if (match && isSuccess(loadingState)) {
            const parsed = parse(location.search) as { search: string, after: string };
            const qs = pick<{ search: string, after: string }>(parsed, [ 'search', 'after' ]);

            return (
                <Helmet>
                    {hasNext && <link rel="next" href={baseUrl + url(forumTopicsCategoryUrl, {categorySlug}, {...qs, after: items[items.length - 1].id})} />}
                    {hasPrevious && <link rel="prev" href={baseUrl + url(forumTopicsCategoryUrl, {categorySlug}, { ...qs, after: -items[0].id })} />}
                </Helmet>
            );
        }

        return null;
    };
}
